import React, { useEffect, useState } from "react";
import { grade_all } from "../../../branches/api/common";

// Shared Enquiries inbox UI. Used by both HQ admin (read-only oversight)
// and the branch sub-app (1:1 replies + announcements).
//
// `api` shape:
//   listEnquiries({ kind?, status?, search?, branch_id? }) → { data: [] }
//   getEnquiry(id, extra?)                                 → { data: {...} }
//   reply({ message_id, body, attachment_url, ...extra })  → { status }   (branch only)
//   setStatus(id, status, extra?)                          → { status }   (branch only)
//   createAnnouncement({ title, body, recipient_grade_id, attachment_url, ...extra })
//                                                          → { status }   (branch only)
//   uploadAttachment(file)                                 → { data: { url } }   (branch only)
//
// `scope`: "branch" | "admin"

const STATUS_PILL = {
  open: { bg: "#fef3c7", fg: "#92400e", label: "Open" },
  answered: { bg: "#dcfce7", fg: "#166534", label: "Answered" },
  closed: { bg: "#e5e7eb", fg: "#4b5563", label: "Closed" },
};

const fmtWhen = (s) => {
  if (!s) return "";
  const d = new Date(s);
  return d.toLocaleString("en-AU", {
    month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
};

const EnquiriesInbox = ({ api, scope }) => {
  const isBranch = scope === "branch";
  const [tab, setTab] = useState("inquiry"); // "inquiry" | "announcement"
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "", search: "" });
  const [activeId, setActiveId] = useState(null);
  const [activeMsg, setActiveMsg] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [grades, setGrades] = useState([]);

  useEffect(() => {
    // Grades are only needed for the New Announcement compose modal.
    if (isBranch) {
      grade_all().then((r) => {
        if (r?.status) setGrades(r.data || []);
      });
    }
  }, [isBranch]);

  const load = async () => {
    setLoading(true);
    const res = await api.listEnquiries({
      kind: tab,
      status: filter.status || undefined,
      search: filter.search || undefined,
    });
    setRows(res?.data || []);
    setLoading(false);
  };

  const loadOne = async (id) => {
    setActiveId(id);
    setActiveMsg(null);
    const res = await api.getEnquiry(id);
    if (res?.status) setActiveMsg(res.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, filter]);

  return (
    <div className="right-nav" id="dash-event" style={{ padding: "20px 24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div>
          <h4 style={{ fontSize: 18, fontWeight: 600, margin: 0, color: "#1a1a2e" }}>Contact</h4>
          <p style={{ fontSize: 12, color: "#64748b", margin: "4px 0 0" }}>
            {isBranch
              ? "Messages from students and parents · updates you've sent."
              : "Branch contact across all locations."}
          </p>
        </div>
        {isBranch && tab === "announcement" && (
          <button
            type="button"
            onClick={() => setComposeOpen(true)}
            className="btn btn-primary"
            style={{ fontSize: 13 }}
          >
            <i className="fa fa-bullhorn" style={{ marginRight: 6 }} /> New Update
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, borderBottom: "1px solid #e5e7eb", marginBottom: 16 }}>
        {[
          { id: "inquiry", label: "Messages" },
          { id: "announcement", label: "Updates" },
        ].map((t) => (
          <button
            key={t.id} type="button" onClick={() => setTab(t.id)}
            style={{
              fontSize: 13, fontWeight: 600, padding: "8px 16px",
              background: "transparent", border: "none", cursor: "pointer",
              color: tab === t.id ? "#2563eb" : "#64748b",
              borderBottom: tab === t.id ? "2px solid #2563eb" : "2px solid transparent",
              marginBottom: -1,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        {tab === "inquiry" && (
          <select
            value={filter.status}
            onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
            style={{ fontSize: 12, padding: "6px 10px", borderRadius: 6, border: "1px solid #d1d5db", background: "#fff" }}
          >
            <option value="">All status</option>
            <option value="open">Open</option>
            <option value="answered">Answered</option>
            <option value="closed">Closed</option>
          </select>
        )}
        <input
          type="search" placeholder="Search…"
          value={filter.search}
          onChange={(e) => setFilter((f) => ({ ...f, search: e.target.value }))}
          style={{ fontSize: 12, padding: "6px 10px", borderRadius: 6, border: "1px solid #d1d5db", minWidth: 220 }}
        />
      </div>

      {/* List */}
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
        <ListHeader tab={tab} isBranch={isBranch} />
        {loading ? (
          <div style={{ padding: 20, color: "#94a3b8", fontSize: 13 }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 24, color: "#94a3b8", fontSize: 13 }}>
            {tab === "inquiry" ? "No messages yet." : "No updates yet."}
          </div>
        ) : rows.map((m) => (
          <Row key={m.id} m={m} tab={tab} isBranch={isBranch} onOpen={() => loadOne(m.id)} />
        ))}
      </div>

      {activeId && (
        <EnquiryModal
          api={api} scope={scope}
          msg={activeMsg}
          onClose={() => { setActiveId(null); setActiveMsg(null); }}
          onChanged={async () => { await loadOne(activeId); await load(); }}
        />
      )}

      {composeOpen && isBranch && (
        <AnnouncementModal
          api={api} grades={grades}
          onClose={() => setComposeOpen(false)}
          onCreated={async () => { setComposeOpen(false); await load(); }}
        />
      )}
    </div>
  );
};

const GRID_INQUIRY = "1.4fr 1fr 0.6fr 110px 90px";
const GRID_ANNOUNCEMENT = "1.6fr 1fr 0.8fr 0.8fr";

const ListHeader = ({ tab, isBranch }) => {
  if (tab === "inquiry") {
    return (
      <div style={headerRowStyle(GRID_INQUIRY)}>
        <span>Subject</span>
        <span>From</span>
        <span>{isBranch ? "Last activity" : "Branch"}</span>
        <span>Status</span>
        <span style={{ textAlign: "right" }}>Replies</span>
      </div>
    );
  }
  return (
    <div style={headerRowStyle(GRID_ANNOUNCEMENT)}>
      <span>Title</span>
      <span>{isBranch ? "Target" : "Branch · Target"}</span>
      <span>Sent by</span>
      <span>Sent at</span>
    </div>
  );
};

const Row = ({ m, tab, isBranch, onOpen }) => {
  if (tab === "inquiry") {
    const s = STATUS_PILL[m.status] || STATUS_PILL.open;
    return (
      <button type="button" onClick={onOpen} style={rowBtnStyle(GRID_INQUIRY)}
        onMouseEnter={(e) => (e.currentTarget.style.background = "#f9fafb")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      >
        <span style={titleCell}>{m.title}</span>
        <span style={{ fontSize: 12 }}>
          {m.initiator_first_name || ""} {m.initiator_last_name || ""}
        </span>
        <span style={{ fontSize: 12, color: "#64748b" }}>
          {isBranch ? fmtWhen(m.last_reply_at || m.create_at) : (m.branch_name || "—")}
        </span>
        <span>
          <span style={{
            fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999,
            background: s.bg, color: s.fg, textTransform: "uppercase",
          }}>{s.label}</span>
        </span>
        <span style={{ textAlign: "right", fontSize: 12, color: "#94a3b8" }}>
          {m.reply_count || 0}
        </span>
      </button>
    );
  }
  // Announcement row
  const targetLabel = m.recipient_grade_id
    ? `Year ${m.grade_name || m.recipient_grade_id}`
    : "Whole branch";
  return (
    <button type="button" onClick={onOpen} style={rowBtnStyle(GRID_ANNOUNCEMENT)}
      onMouseEnter={(e) => (e.currentTarget.style.background = "#f9fafb")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      <span style={titleCell}>{m.title}</span>
      <span style={{ fontSize: 12, color: "#64748b" }}>
        {isBranch ? targetLabel : `${m.branch_name || "—"} · ${targetLabel}`}
      </span>
      <span style={{ fontSize: 12 }}>
        {m.initiator_first_name || ""} {m.initiator_last_name || ""}
      </span>
      <span style={{ fontSize: 12, color: "#64748b" }}>{fmtWhen(m.create_at)}</span>
    </button>
  );
};

const headerRowStyle = (grid) => ({
  display: "grid", gridTemplateColumns: grid,
  padding: "10px 14px", background: "#f9fafb",
  fontSize: 11, color: "#6b7280", fontWeight: 600,
  textTransform: "uppercase", letterSpacing: 0.5,
});

const rowBtnStyle = (grid) => ({
  display: "grid", gridTemplateColumns: grid,
  padding: "12px 14px", borderTop: "1px solid #f1f5f9", alignItems: "center",
  fontSize: 13, color: "#334155", textAlign: "left",
  background: "transparent", border: "none",
  width: "100%", cursor: "pointer",
});

const titleCell = {
  fontWeight: 500, color: "#1e293b",
  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};

// -------- Enquiry modal (inquiry: replyable, announcement: read-only) --------

const EnquiryModal = ({ api, scope, msg, onClose, onChanged }) => {
  const isBranch = scope === "branch";
  const [reply, setReply] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!msg) return;
    if (!reply.trim() && !file) return;
    setBusy(true);
    let attachment_url = null;
    if (file) {
      const up = await api.uploadAttachment(file);
      if (up?.status) attachment_url = up.data?.url;
      else { setBusy(false); return; }
    }
    const res = await api.reply({ message_id: msg.id, body: reply, attachment_url });
    setBusy(false);
    if (res?.status) {
      setReply("");
      setFile(null);
      onChanged();
    }
  };

  const setStatus = async (status) => {
    if (busy) return;
    setBusy(true);
    await api.setStatus(msg.id, status);
    setBusy(false);
    onChanged();
  };

  const isAnnouncement = msg?.kind === "announcement";

  return (
    <div onClick={onClose} style={modalOverlay}>
      <div onClick={(e) => e.stopPropagation()} style={modalCard(720)}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, gap: 12 }}>
          <div>
            <h5 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#1f2937" }}>
              {msg?.title || "Loading…"}
            </h5>
            {msg && (
              <p style={{ margin: "4px 0 0", fontSize: 12, color: "#64748b" }}>
                {isAnnouncement ? (
                  <>
                    Update · {msg.recipient_grade_id ? `Year ${msg.grade_name || msg.recipient_grade_id}` : "Whole branch"}
                    {msg.branch_name ? ` · ${msg.branch_name}` : ""}
                    {" · "}{fmtWhen(msg.create_at)}
                  </>
                ) : (
                  <>
                    From {msg.initiator_first_name} {msg.initiator_last_name}
                    {msg.branch_name ? ` · ${msg.branch_name}` : ""}
                  </>
                )}
              </p>
            )}
          </div>
          <button onClick={onClose} type="button"
            style={{ background: "transparent", border: "none", fontSize: 22, color: "#94a3b8", cursor: "pointer", padding: 0 }}>×</button>
        </div>

        {!msg ? (
          <p style={{ color: "#94a3b8", fontSize: 13 }}>Loading…</p>
        ) : isAnnouncement ? (
          // Read-only announcement view
          <div>
            {msg.body && <div style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap", color: "#1f2937" }}>{msg.body}</div>}
            {msg.attachment_url && (
              <a href={msg.attachment_url} target="_blank" rel="noopener noreferrer"
                 style={{ display: "inline-block", marginTop: 12, fontSize: 12, color: "#2563eb" }}>
                <i className="fa fa-paperclip" style={{ marginRight: 4 }} /> Attachment
              </a>
            )}
          </div>
        ) : (
          // Inquiry thread
          <>
            {msg.body && (
              <Bubble role="student"
                name={`${msg.initiator_first_name || ""} ${msg.initiator_last_name || ""}`.trim() || "Student"}
                body={msg.body} attachment={msg.attachment_url} when={msg.create_at} />
            )}
            {msg.replies?.map((r) => (
              <Bubble key={r.id} role={r.role}
                name={r.role === "student"
                  ? (r.first_name || "Student")
                  : (r.first_name || (r.role === "branch" ? "Branch" : "CS Education"))}
                body={r.body} attachment={r.attachment_url} when={r.create_at} />
            ))}

            {isBranch && (
              <div style={{ display: "flex", gap: 8, marginTop: 14, marginBottom: 12, flexWrap: "wrap" }}>
                {msg.status !== "answered" && (
                  <button type="button" onClick={() => setStatus("answered")}
                    style={chipBtn("#dcfce7", "#166534")} disabled={busy}>Mark answered</button>
                )}
                {msg.status !== "closed" && (
                  <button type="button" onClick={() => setStatus("closed")}
                    style={chipBtn("#e5e7eb", "#4b5563")} disabled={busy}>Close thread</button>
                )}
                {msg.status === "closed" && (
                  <button type="button" onClick={() => setStatus("open")}
                    style={chipBtn("#fef3c7", "#92400e")} disabled={busy}>Reopen</button>
                )}
              </div>
            )}

            {isBranch && msg.status !== "closed" && (
              <form onSubmit={submit} style={{ borderTop: "1px solid #f1f5f9", paddingTop: 12 }}>
                <textarea className="form-control" rows="3" placeholder="Reply…"
                  style={{ fontSize: 13 }}
                  value={reply} onChange={(e) => setReply(e.target.value)} disabled={busy}></textarea>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                  <input type="file" accept="image/*,application/pdf"
                    onChange={(e) => setFile(e.target.files?.[0] || null)} disabled={busy}
                    style={{ fontSize: 12 }} />
                  <button type="submit" className="btn btn-primary" style={{ fontSize: 13 }}
                    disabled={busy || (!reply.trim() && !file)}>Send reply</button>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// -------- New Announcement compose modal --------

const AnnouncementModal = ({ api, grades, onClose, onCreated }) => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [gradeId, setGradeId] = useState(""); // "" = whole branch
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    setBusy(true);
    let attachment_url = null;
    if (file) {
      const up = await api.uploadAttachment(file);
      if (up?.status) attachment_url = up.data?.url;
      else { setBusy(false); return; }
    }
    const res = await api.createAnnouncement({
      title: title.trim(),
      body: body.trim(),
      recipient_grade_id: gradeId || null,
      attachment_url,
    });
    setBusy(false);
    if (res?.status) onCreated();
  };

  return (
    <div onClick={onClose} style={modalOverlay}>
      <div onClick={(e) => e.stopPropagation()} style={modalCard(720)}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h5 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#1f2937" }}>
            New Update
          </h5>
          <button onClick={onClose} type="button"
            style={{ background: "transparent", border: "none", fontSize: 22, color: "#94a3b8", cursor: "pointer", padding: 0 }}>×</button>
        </div>

        <form onSubmit={submit}>
          <Field label="Send to *">
            <select className="form-control" style={{ fontSize: 13 }}
              value={gradeId} onChange={(e) => setGradeId(e.target.value)} disabled={busy}>
              <option value="">Whole branch (all students)</option>
              {grades.map((g) => (
                <option key={g.id} value={g.id}>Year {g.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Title *">
            <input type="text" className="form-control" style={{ fontSize: 13 }}
              value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Term 3 schedule change" required disabled={busy} />
          </Field>
          <Field label="Message *">
            <textarea className="form-control" rows="6" style={{ fontSize: 13 }}
              value={body} onChange={(e) => setBody(e.target.value)}
              placeholder="Write your announcement…" required disabled={busy}></textarea>
          </Field>
          <Field label="Attachment (image or PDF, optional)">
            <input type="file" accept="image/*,application/pdf" style={{ fontSize: 13 }}
              onChange={(e) => setFile(e.target.files?.[0] || null)} disabled={busy} />
          </Field>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <button type="button" className="btn btn-secondary" style={{ fontSize: 13 }}
              onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className="btn btn-primary" style={{ fontSize: 13 }}
              disabled={busy || !title.trim() || !body.trim()}>Send</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 12 }}>
    <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6, display: "block" }}>
      {label}
    </label>
    {children}
  </div>
);

const modalOverlay = {
  position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
  display: "flex", alignItems: "center", justifyContent: "center",
  zIndex: 1050, padding: 24, overflowY: "auto",
};

const modalCard = (maxWidth) => ({
  background: "#fff", borderRadius: 10, maxWidth, width: "100%",
  padding: "20px 24px", boxShadow: "0 20px 50px rgba(15,23,42,0.25)",
  maxHeight: "calc(100vh - 48px)", overflowY: "auto",
});

const chipBtn = (bg, fg) => ({
  fontSize: 11, fontWeight: 600, padding: "5px 12px", borderRadius: 999,
  background: bg, color: fg, border: "none", cursor: "pointer",
});

const Bubble = ({ role, name, body, attachment, when }) => {
  const isStudent = role === "student";
  return (
    <div style={{ display: "flex", justifyContent: isStudent ? "flex-start" : "flex-end", marginBottom: 12 }}>
      <div style={{
        maxWidth: "78%",
        background: isStudent ? "#f1f5f9" : "#dbeafe",
        color: "#1f2937",
        borderRadius: 10, padding: "10px 14px", fontSize: 13, lineHeight: 1.5,
      }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: isStudent ? "#475569" : "#1e40af", marginBottom: 4 }}>
          {name}
          <span style={{ fontWeight: 400, marginLeft: 8, color: "#94a3b8" }}>{fmtWhen(when)}</span>
        </div>
        {body && <div style={{ whiteSpace: "pre-wrap" }}>{body}</div>}
        {attachment && (
          <a href={attachment} target="_blank" rel="noopener noreferrer"
             style={{ display: "inline-block", marginTop: body ? 8 : 0, fontSize: 12, color: "#2563eb" }}>
            <i className="fa fa-paperclip" style={{ marginRight: 4 }} /> Attachment
          </a>
        )}
      </div>
    </div>
  );
};

export default EnquiriesInbox;
